import { useCallback, useState } from "react";
import {
    Container,
    ContainerSize,
    Form,
    Heading,
    HtmlEditor,
    Text,
    Wrapper,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const initialHtml = "<p>Edit <strong>this</strong> sample. Use the toolbar for formatting.</p>";

const HtmlEditorLiveDemo = () => {
    const [html, setHtml] = useState(initialHtml);
    const onChange = useCallback((next: string) => setHtml(next), []);

    return (
        <Form.Group>
            <Form.Label id="html-editor-demo-label">Message</Form.Label>
            <HtmlEditor
                className="mb-3"
                defaultValue={initialHtml}
                onChange={onChange}
                ariaLabelledBy="html-editor-demo-label"
            />
            <Form.Label htmlFor="html-editor-output" className="text-muted small">
                HTML source
            </Form.Label>
            <Form.Control
                el="textarea"
                id="html-editor-output"
                readOnly
                rows={5}
                className="font-monospace small mb-3"
                value={html}
            />
            <Form.Label id="html-editor-preview-label" className="text-muted small">
                Preview (rendered HTML)
            </Form.Label>
            <div
                id="html-editor-preview"
                className="border rounded p-3 bg-body-secondary"
                aria-labelledby="html-editor-preview-label"
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </Form.Group>
    );
};

const HtmlEditorPage = () => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                HTML editor
            </Heading>
            <Text className="mb-4">
                A lightweight rich-text field using <Text el="code">contentEditable</Text> and{" "}
                <Text el="code">document.execCommand</Text> (deprecated but still useful for small demos). Toolbar
                groups mirror a typical WYSIWYG config—see <Text el="code">htmlEditorToolbar</Text> (exported with{" "}
                <Text el="code">HtmlEditor</Text>). Selection is saved on <Text el="code">keyup</Text> /{" "}
                <Text el="code">mouseup</Text> so dropdowns can apply block type and font size to the last caret
                range. For production, prefer a maintained editor (e.g.{" "}
                <Wrapper
                    el="a"
                    href="https://tiptap.dev/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Tiptap
                </Wrapper>
                ).
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Toolbar
            </Heading>
            <Text className="mb-4">
                Inline (bold, italic, underline), block type, pixel font sizes, lists, and alignment—each driven from{" "}
                <Text el="code">htmlEditorToolbar</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <HtmlEditorLiveDemo />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {htmlEditorExampleCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default HtmlEditorPage;

const htmlEditorExampleCode = `const initialHtml =
  "<p>Edit <strong>this</strong> sample. Use the toolbar for formatting.</p>";

const [html, setHtml] = useState(initialHtml);

<Form.Group>
  <Form.Label id="html-editor-demo-label">Message</Form.Label>
  <HtmlEditor
    className="mb-3"
    defaultValue={initialHtml}
    onChange={setHtml}
    ariaLabelledBy="html-editor-demo-label"
  />
  <Form.Label htmlFor="html-editor-output">HTML source</Form.Label>
  <Form.Control
    el="textarea"
    id="html-editor-output"
    readOnly
    rows={5}
    className="font-monospace small mb-3"
    value={html}
  />
  <Form.Label id="html-editor-preview-label">Preview (rendered HTML)</Form.Label>
  <div
    id="html-editor-preview"
    className="border rounded p-3 bg-body-secondary"
    aria-labelledby="html-editor-preview-label"
    dangerouslySetInnerHTML={{ __html: html }}
  />
</Form.Group>`;
